// People: everyone in the owner's life, and one page each that gathers what
// was scattered: their dates, what waits on them, what is with or for them,
// what they sent, notes, and their share link. Nothing is counted; every
// section is only there when it has something in it.
import { useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { CalendarPlus, ChevronRight, Link2, Pencil, Plus } from 'lucide-react'
import {
  useAllEdges,
  useAllProjects,
  useAllTasks,
  useDates,
  useGrants,
  usePeople,
  useProjectOf,
} from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { dateOf } from '@/lib/time'
import { gather, handlesOf } from '@/lib/people'
import { rowLine, upcomingDates } from '@/lib/dates'
import { CIRCLES, type Circle } from '@/kinds/person/schema'
import type { PersonResource } from '@/kinds/person/apply'
import { mutate } from '@/runtime'
import { addTaskFromText } from '@/components/QuickAdd'
import { Avatar, CIRCLE_LABEL, PersonSheet, circleOne } from '@/components/PersonBits'
import { DateRow } from '@/components/DateBits'
import { TaskRow } from '@/components/TaskRow'
import { ShareSheet } from '@/components/ShareSheet'
import { ActionBar, type ActionItem } from '@/components/ActionBar'
import { Button, Empty, PageTitle, SectionTitle, cx } from '@/components/ui'

export const PeopleView = () => {
  const { id } = useParams({ strict: false }) as { id?: string }
  const people = usePeople()
  if (id && id !== 'new') {
    const p = people.find((x) => x.id === id)
    // a person deleted on another device, or a link to nobody: back to everyone
    if (p) return <PersonPage person={p} />
  }
  return <PeopleList adding={id === 'new'} people={people} />
}

const PeopleList = ({ adding, people }: { adding: boolean; people: PersonResource[] }) => {
  const navigate = useNavigate()
  const dates = useDates()
  const edges = useAllEdges()
  const today = dateOf(new Date(useNow(60_000)))
  // each person's next date, for the line under their name
  const next = new Map<string, string>()
  const coming = upcomingDates(dates, today, 366)
  for (const u of coming)
    for (const e of edges)
      if (e.from_id === u.date.id && e.relation === 'references' && !next.has(e.to_id))
        next.set(e.to_id, `${u.date.spec.title}: ${rowLine(u.date.spec, u.on, today).toLowerCase()}`)
  // you first, on your own; then everyone else by circle
  const others = people.filter((p) => !p.spec.me)
  const groups: [Circle | 'none' | 'me', PersonResource[]][] = [
    ['me', people.filter((p) => p.spec.me)],
    ...CIRCLES.map((c) => [c, others.filter((p) => p.spec.circle === c)] as [Circle, PersonResource[]]),
    ['none', others.filter((p) => !p.spec.circle)],
  ]
  return (
    <div className="flex flex-col gap-5" data-testid="people">
      <PageTitle
        right={
          <Button
            variant="primary"
            onClick={() => void navigate({ to: '/people/$id', params: { id: 'new' } })}
            data-testid="person-new"
          >
            <Plus size={16} /> Add someone
          </Button>
        }
      >
        People
      </PageTitle>
      {people.length === 0 && (
        <Empty icon="☺">
          The people in your life: their birthdays, what you’re waiting on them for, what they’ve sent you.
          Add someone, or tell Prime about them.
        </Empty>
      )}
      {groups.map(([c, list]) =>
        list.length ? (
          <section key={c}>
            {groups.filter(([, l]) => l.length).length > 1 && (
              <SectionTitle>
                {c === 'me' ? 'You' : c === 'none' ? 'Everyone else' : CIRCLE_LABEL[c]}
              </SectionTitle>
            )}
            <ul className="group-list">
              {list.map((p) => (
                <li key={p.id}>
                  <Link
                    to="/people/$id"
                    params={{ id: p.id }}
                    className="flex items-center gap-3 px-3.5 min-h-14 py-2 hover:bg-raised/50"
                    data-testid="person-row"
                  >
                    <Avatar person={p} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] truncate">{p.spec.name}</span>
                      {next.get(p.id) && (
                        <span className="block text-xs text-faint truncate">{next.get(p.id)}</span>
                      )}
                    </span>
                    <ChevronRight size={16} className="shrink-0 text-faint" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null,
      )}
      {adding && (
        <PersonSheet
          onDone={(made) =>
            void navigate(made ? { to: '/people/$id', params: { id: made } } : { to: '/people' })
          }
        />
      )}
    </div>
  )
}

const FROM_CAP = 5

const PersonPage = ({ person }: { person: PersonResource }) => {
  const navigate = useNavigate()
  const tasks = useAllTasks() ?? []
  const projects = useAllProjects() ?? []
  const byProject = new Map(projects.map((p) => [p.id, p]))
  const projectOf = useProjectOf()
  const dates = useDates()
  const edges = useAllEdges()
  const grants = useGrants()
  const today = dateOf(new Date(useNow(60_000)))
  const v = gather(person, tasks, dates, edges, today)
  const [editing, setEditing] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [allFrom, setAllFrom] = useState(false)
  const [draft, setDraft] = useState('')
  const handles = new Set(handlesOf(person))
  const links = grants.filter(
    (g) => !g.status.revoked_at && g.spec.from && handles.has(g.spec.from.toLowerCase()),
  )
  const me = !!person.spec.me
  const first = person.spec.name.split(/\s+/)[0]

  const addTask = async () => {
    const text = draft.trim()
    if (!text) return
    setDraft('')
    const r = await addTaskFromText(text, projects)
    if (r) await mutate.setEdge(r.id, person.id, 'references')
  }
  const actions: ActionItem[] = [
    {
      id: 'add-date',
      label: 'Add a date',
      icon: CalendarPlus,
      pin: true,
      onSelect: () =>
        void navigate({ to: '/dates/$id', params: { id: 'new' }, search: { who: person.id } as never }),
      testid: 'person-add-date',
    },
    {
      id: 'share',
      label: links.length ? 'New share link' : 'Share link',
      icon: Link2,
      hidden: me,
      onSelect: () => setSharing(true),
      testid: 'person-share',
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: Pencil,
      pin: true,
      onSelect: () => setEditing(true),
      testid: 'person-edit',
    },
  ]
  const row = (t: (typeof tasks)[number]) => (
    <TaskRow key={t.id} task={t} project={byProject.get(projectOf.get(t.id) ?? '')} mode="compact" />
  )

  return (
    <div className="flex flex-col gap-5" data-testid="person">
      <header className="flex items-center gap-4">
        <Avatar person={person} size={64} />
        <div className="min-w-0 flex-1">
          <h1 className="t-title truncate">{person.spec.name}</h1>
          <p className="text-[13px] text-faint mt-0.5 flex flex-wrap gap-x-3">
            {me && <span>You</span>}
            {person.spec.circle && <span>{circleOne(person.spec.circle)}</span>}
            <span>@{person.spec.key}</span>
          </p>
        </div>
      </header>

      <ActionBar className="-mt-1" items={actions} label="Person actions" testid="person-actions" />

      {v.dates.length > 0 && (
        <section>
          <SectionTitle>Dates</SectionTitle>
          <ul className="group-list">
            {v.dates.map((d) => (
              <DateRow key={d.date.id} date={d.date} on={d.on} today={today} />
            ))}
          </ul>
        </section>
      )}

      {v.waiting.length > 0 && (
        <section>
          <SectionTitle>{me ? 'Waiting on you' : `Waiting on ${first}`}</SectionTitle>
          <ul className="group-list">{v.waiting.map(row)}</ul>
        </section>
      )}

      <section>
        <SectionTitle>{me ? 'Yours' : `With ${first}`}</SectionTitle>
        <ul className="group-list" data-testid="person-with">
          {v.with.map(row)}
          <li>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void addTask()
              }}
              className="flex items-center gap-3 px-3.5 min-h-12"
            >
              <Plus size={16} className="text-faint shrink-0" aria-hidden />
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={me ? 'Something for you' : `Something to do with ${first}`}
                className="bare-input flex-1 min-w-0 text-[15px]"
                enterKeyHint="done"
                data-testid="person-add-task"
              />
            </form>
          </li>
        </ul>
      </section>

      {v.from.length > 0 && (
        <section>
          <SectionTitle
            right={
              !allFrom && v.from.length > FROM_CAP ? (
                <button
                  type="button"
                  className="text-[13px] text-accent-text"
                  onClick={() => setAllFrom(true)}
                >
                  Show all
                </button>
              ) : undefined
            }
          >
            From {first}
          </SectionTitle>
          <ul className="group-list">{(allFrom ? v.from : v.from.slice(0, FROM_CAP)).map(row)}</ul>
        </section>
      )}

      <section>
        <SectionTitle>Notes</SectionTitle>
        <textarea
          key={person.id + (person.clocks['spec.notes'] ?? '')}
          defaultValue={person.spec.notes ?? ''}
          onBlur={(e) => {
            const notes = e.target.value.trim()
            if (notes !== (person.spec.notes ?? ''))
              void mutate.patch(person.id, { spec: { notes: notes || null } })
          }}
          rows={4}
          placeholder="Gift ideas, sizes, what they like, what to ask about"
          className={cx('w-full')}
          data-testid="person-notes"
        />
      </section>

      {links.length > 0 && (
        <p className="text-xs text-faint -mt-2">
          {links.length === 1 ? 'Their link is' : 'Their links are'} live: what they send lands here. Revoke
          in Settings → Sharing.
        </p>
      )}

      {editing && <PersonSheet resource={person} onDone={() => setEditing(false)} />}
      {sharing && (
        <ShareSheet
          person={{ name: person.spec.name, key: person.spec.key }}
          onClose={() => setSharing(false)}
        />
      )}
    </div>
  )
}
